import * as React from 'react';
import {
  TypeImplementation,
  Expressions,
  withSystem,
  ExpressionPath,
} from '@packages/systems/wfdl';

import {getElementFromNodeInInstanceNativeId} from '@packages/utilities/platform';
import {
  isStringElement,
  isLinkBlock,
} from '@packages/utilities/elementTypeUtils';
import {
  TList,
  TElement,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {getRootElementWithFocusedInstancesExpanded} from '@packages/utilities/platform-selectors';
import {createSelector} from 'reselect';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';

import {updateAnn} from '@packages/utilities/contextual-expression';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';

// This is a one off util for selecting the _non_ staged components version
// of the currently selected element. The code here is equal to `platform/selectors`
// selectSelectedElement in all other ways than the targeted components map is never
// the staged one.
// The reason this is necessary and we can not use the common util for selecting the
// selected element is that we want to update the value only after it has been updated
// to the real componentMap in order to correctly handle actions submitted by the
// children input.
// Do not remove this methods usage without validating the use cases laid down in
// the render function.
const selectSelectedElementNonStaged = createSelector(
  (state: DesignerStores) => state.DesignerStore.components,
  (state: DesignerStores) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  (state: DesignerStores) => state.UiNodeStore.selectedNativeIdPath,
  (state: DesignerStores) => state.UiNodeStore.focusedSymbolNativeIdPath,
  (
    componentMap,
    selectedNodeNativeId,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath
  ) => {
    if (selectedNativeIdPath?.length > 0 && componentMap) {
      const element = getElementFromNodeInInstanceNativeId(
        selectedNativeIdPath,
        componentMap
      );
      return element;
    }
    const rootElement = getRootElementWithFocusedInstancesExpanded(
      componentMap,
      focusedSymbolNativeIdPath
    );
    return selectedNodeNativeId != null && rootElement
      ? Expressions.getElementById(rootElement, selectedNodeNativeId)
      : null;
  }
);

function hasStringOrBoundChild(element: ExpressionElement): boolean {
  const elementData = Expressions.getElementData(element);
  const childrenData = elementData
    ? Expressions.getIn(elementData, CHILDREN_PATH)
    : undefined;

  const children = childrenData ? Expressions.getList(childrenData) : null;

  const firstChild = children ? children[0] : null;

  const firstChildEl = firstChild ? Expressions.getElement(firstChild) : null;
  const isStringChild = firstChildEl ? isStringElement(firstChildEl) : false;

  // If `children` is null it means that it is not just a raw list of elements
  // aka it is a binding
  return children === null || isStringChild;
}

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

export const LinkChildren = TypeImplementation.Alias({
  type: TList(TElement),
  render: function ListChildrenRender({expr, dispatch}) {
    const element = useDesignerSelector(selectSelectedElementNonStaged);

    // For Link Block elements we don't want to render children settings.
    // An expection to this rule is if there is an existing binding on the element,
    // or if the children is a String element, this indicates that the children
    // have been added in via the settings panel and we want to retain those.
    // This logic is duplicated in packages/systems/user-components/properties/utils.ts for Suggested Props
    if (element && isLinkBlock(element) && !hasStringOrBoundChild(element)) {
      return null;
    }

    // In order to render the binding menu for the subsequent expression
    // editor render we need to change the type to now say that this field
    // is bindable.
    const nextExpr = updateAnn((ann) => {
      const type = ann.type;
      if (!type) {
        return ann;
      }
      return {
        type: withSystem(
          {
            label: 'Text',
            isBindable: true,
          },
          type
        ),

        env: ann.env,

        errors: ann.errors,
      };
    }, expr);
    return <ExpressionEditor expression={nextExpr} dispatch={dispatch} />;
  },
  system: {
    // LinkChildren manages its own binding UI
    bindUIVariant: 'custom',
  },
});
